import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead, Field, Input, Select, Textarea, SubmitButton, usePageMeta } from "@/components/shared";
import { submitLead } from "@/lib/api";

export default function Consultation() {
  usePageMeta("Book a Consultation", "Book a consultation with The Bermuda Co-Host — a relaxed, honest conversation about your Bermuda property.");
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_contact: "", preferred_date: "", preferred_time: "", topic: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead("consultation", form);
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <section className="mx-auto max-w-[900px] px-6 pb-28 pt-40 lg:px-10">
        <SectionHead
          overline="Book a Consultation"
          title={<>A conversation,<br /><span className="italic text-bermuda-dark">not a pitch.</span></>}
          copy="Tell us when suits you and what you'd like to discuss. We'll confirm your consultation personally."
        />
        <Reveal delay={0.15}>
          <div className="mt-12 border border-navy/10 bg-white p-8 shadow-xl shadow-navy/5 lg:p-12">
            {done ? (
              <div className="py-8 text-center" data-testid="consultation-success">
                <CheckCircle2 size={40} strokeWidth={1.2} className="mx-auto text-bermuda-dark" />
                <h3 className="mt-6 font-serif text-3xl text-navy">Consultation requested.</h3>
                <p className="mx-auto mt-4 max-w-sm text-sm text-navy/60">
                  Thank you — we will confirm your consultation time personally, using your preferred contact method.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="grid gap-5 sm:grid-cols-2" data-testid="consultation-form">
                <Field label="Full Name" required><Input data-testid="consultation-name" required value={form.name} onChange={set("name")} /></Field>
                <Field label="Email" required><Input data-testid="consultation-email" required type="email" value={form.email} onChange={set("email")} /></Field>
                <Field label="Phone" required><Input data-testid="consultation-phone" required type="tel" value={form.phone} onChange={set("phone")} /></Field>
                <Field label="Preferred Contact Method" required>
                  <Select data-testid="consultation-contact-method" required value={form.preferred_contact} onChange={set("preferred_contact")}>
                    <option value="">Select…</option><option>Phone call</option><option>WhatsApp / Text</option><option>Email</option><option>Video call</option>
                  </Select>
                </Field>
                <Field label="Preferred Date"><Input data-testid="consultation-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} /></Field>
                <Field label="Preferred Time"><Input data-testid="consultation-time" type="time" value={form.preferred_time} onChange={set("preferred_time")} /></Field>
                <div className="sm:col-span-2">
                  <Field label="What Would You Like to Discuss?" required>
                    <Select data-testid="consultation-topic" required value={form.topic} onChange={set("topic")}>
                      <option value="">Select…</option>
                      <option>Co-hosting my existing listing</option>
                      <option>Full property management</option>
                      <option>Cleaning services</option>
                      <option>Maintenance or inspections</option>
                      <option>Starting a new vacation rental</option>
                      <option>Something else</option>
                    </Select>
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Anything Else We Should Know?"><Textarea data-testid="consultation-message" value={form.message} onChange={set("message")} /></Field>
                </div>
                <div className="sm:col-span-2">
                  <SubmitButton loading={loading} testid="consultation-submit">Book My Consultation</SubmitButton>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </main>
  );
}
